import React, { createContext, useCallback, useContext, useRef } from 'react';
import { Portal as ArkPortal, type PortalProps } from '@ark-ui/react/portal';
import { EnvironmentProvider } from '@ark-ui/react/environment';

export const PortalContainerContext = createContext<
  React.RefObject<HTMLElement | null> | undefined
>(undefined);

const WRAPPER_STYLE: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  minHeight: '100%',
};

const PORTAL_ROOT_STYLE: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  zIndex: 10000,
};

// :where() drží specificitu na nule — pravidlo jen obnovuje
// klikatelnost po `pointer-events: none` na kontejneru a nesmí
// přebít styly komponent (např. Drawer positioner).
const PORTAL_ROOT_PIERCE_STYLE = {
  __html: `:where(.penta-portal-root > *) { pointer-events: auto; }`,
};

export const ScopedPortalProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  // Stabilní reference — EnvironmentProvider hodnotu porovnává identitou,
  // nová funkce při každém renderu by zbytečně přerenderovávala konzumenty.
  const getRootNode = useCallback(
    () => ref.current?.ownerDocument ?? document,
    [],
  );

  return (
    <EnvironmentProvider value={getRootNode}>
      <PortalContainerContext.Provider value={ref}>
        <div style={WRAPPER_STYLE}>
          {children}

          <div
            ref={ref}
            className="penta-portal-root"
            style={PORTAL_ROOT_STYLE}
          />
          {/* :where() drží specificitu na nule — pravidlo jen obnovuje
              klikatelnost po `pointer-events: none` na kontejneru a nesmí
              přebít styly komponent (např. Drawer positioner). */}
          <style dangerouslySetInnerHTML={PORTAL_ROOT_PIERCE_STYLE} />
        </div>
      </PortalContainerContext.Provider>
    </EnvironmentProvider>
  );
};

export const Portal: React.FC<React.PropsWithChildren<PortalProps>> = ({
  children,
  ...props
}) => {
  const container = useContext(PortalContainerContext);
  return (
    <ArkPortal container={container} {...props}>
      {children}
    </ArkPortal>
  );
};
